import imagePath from "@/src/paths/imagePath.js";
import { imageNames } from "@/src/constants/imageConstants.js";

export const overflowOptions = {
  SCROLL: "scroll",
  AUTO: "auto",
  HIDDEN: "hidden",
  VISIBLE: "visible",
};

export const variants = {
  VARIANT1: "variant1",
  VARIANT2: "variant2",
  VARIANT3: "variant3",
  VARIANT4: "variant4",
};

export const componentSizes = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
};

export const gitInstructionsSizeConfig = {
  small: {
    outerGap: "gap-6",
    label: "text-sm",
    content: "text-xs",
    row: "pl-2 pr-1.5 py-1",
    sectionGap: "gap-4",
    innerGap: "gap-1",
    icon: "size-3",
    button: "primary_small",
  },
  medium: {
    outerGap: "gap-10",
    label: "text-lg",
    content: "text-sm",
    row: "pl-3 pr-2 py-[6px]",
    sectionGap: "gap-8",
    innerGap: "gap-1",
    icon: "size-4",
    button: "primary_medium",
  },
  large: {
    outerGap: "gap-12",
    label: "text-xl",
    content: "text-base",
    row: "pl-4 pr-3 py-2",
    sectionGap: "gap-10",
    innerGap: "gap-2",
    icon: "size-5",
    button: "primary_large",
  },
};

export const positionOptions = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
];

export const maxFileSizes = {
  audio: 5 * 1024 * 1024,
  image: 5 * 1024 * 1024,
  video: 50 * 1024 * 1024,
  document: 5 * 1024 * 1024,
  esign_doc: 25 * 1024 * 1024,
};

export const customSelectorDirections = {
  VERTICAL: "vertical",
  HORIZONTAL: "horizontal",
};

export const customComponentDirections = {
  VERTICAL: "vertical",
  HORIZONTAL: "horizontal",
};

export const customSelectorHorizontalAlignOptions = {
  START: "items-start",
  CENTER: "items-center",
  END: "items-end",
};

export const filterStates = {
  INACTIVE: "inactive",
  ACTIVE: "active",
  SELECTED: "selected",
};

export const filterStateMapping = {
  [filterStates.INACTIVE]: {
    id: filterStates.INACTIVE,
    icon: imageNames.FILTER_GREY,
  },
  [filterStates.ACTIVE]: {
    id: filterStates.ACTIVE,
    icon: imageNames.FILTER_ACTIVE,
  },
  [filterStates.SELECTED]: {
    id: filterStates.SELECTED,
    icon: imageNames.FILTER_SELECTED,
  },
};

export const statusTagProviderColors = {
  GREY: "grey",
  PURPLE: "purple",
  GREEN: "green",
  YELLOW: "yellow",
  RED: "red",
  BLUE: "blue",
  BROWN: "brown",
  ORANGE: "orange",
};

export const priorityTagProviderColors = {
  GREY: "grey",
  WHITE: "white",
  BLUE: "blue",
  BLUE_8: "blue_8",
  BLUE_14: "blue_14",
  GREEN: "green",
  GREEN_8: "green_8",
  GREEN_14: "green_14",
  ORANGE: "orange",
  ORANGE_8: "orange_8",
  ORANGE_14: "orange_14",
};

export const statusTagProviderColorMapping = {
  [statusTagProviderColors.GREY]: "var(--grey-tag-bg)",
  [statusTagProviderColors.PURPLE]: "var(--purple-tag-bg)",
  [statusTagProviderColors.GREEN]: "var(--green-tag-bg)",
  [statusTagProviderColors.YELLOW]: "var(--yellow-tag-bg)",
  [statusTagProviderColors.RED]: "var(--red-tag-bg)",
  [statusTagProviderColors.BLUE]: "var(--blue-tag-bg)",
  [statusTagProviderColors.BROWN]: "#8E351A",
};

export const priorityTagProviderColorMapping = {
  [priorityTagProviderColors.GREY]: "var(--grey-tag-bg)",
  [priorityTagProviderColors.WHITE]: "white",
  [priorityTagProviderColors.BLUE]: "var(--priority-tag-blue-text)",
  [priorityTagProviderColors.BLUE_8]: "var(--blue-opacity-8)",
  [priorityTagProviderColors.BLUE_14]: "var(--blue-opacity-14)",
  [priorityTagProviderColors.GREEN]: "var(--priority-tag-green-text)",
  [priorityTagProviderColors.GREEN_8]: "var(--green-opacity-8)",
  [priorityTagProviderColors.GREEN_14]: "var(--green-opacity-14)",
  [priorityTagProviderColors.ORANGE]: "var(--priority-tag-orange-text)",
  [priorityTagProviderColors.ORANGE_8]: "var(--orange-opacity-8)",
  [priorityTagProviderColors.ORANGE_14]: "var(--orange-opacity-14)",
};

export const starSelectorTypes = {
  STAR: "star",
  RATE: "rate",
};

export const generateTextVariations = (baseText) => {
  const variations = new Set();
  const words = baseText.split("_");

  const addVariation = (text) => {
    variations.add(text.toLowerCase());
    variations.add(text.toUpperCase());
    variations.add(text.charAt(0).toUpperCase() + text.slice(1).toLowerCase());
  };

  const baseVariations = [
    baseText,
    words.join(""),
    words.join(" "),
    words.join("-"),
    words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(""),
    words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" "),
    words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join("-"),
  ];

  baseVariations.forEach((variation) => addVariation(variation));

  return Array.from(variations);
};

export const statusTextColorMapping = {
  busy: generateTextVariations("busy"),
  ended: generateTextVariations("ended"),
  skipped: generateTextVariations("skipped"),
  noAnswer: generateTextVariations("no_answer"),
  pending: generateTextVariations("pending"),
  failed: generateTextVariations("failed"),
  paused: generateTextVariations("paused"),
  dialing: generateTextVariations("dialing"),
  callFailed: generateTextVariations("call_failed"),
  cancelled: generateTextVariations("cancelled"),
  voicemail: generateTextVariations("voicemail"),
  connected: generateTextVariations("connected"),
  notStarted: generateTextVariations("not_started"),
  ringing: generateTextVariations("ringing"),
  completed: generateTextVariations("completed"),
  inProgress: generateTextVariations("in_progress"),
  transferred: generateTextVariations("transferred"),
  callCompleted: generateTextVariations("call_completed"),
  voicemailDropped: generateTextVariations("voicemail_dropped"),
  enabled: generateTextVariations("enabled"),
  disabled: generateTextVariations("disabled"),
  deprecated: generateTextVariations("deprecated"),
  draft: generateTextVariations("draft"),
  active: generateTextVariations("active"),
  scheduled: generateTextVariations("scheduled"),
  scheduledProcessing: generateTextVariations("scheduled_processing"),
  sent: generateTextVariations("sent"),
  inactive: generateTextVariations("inactive"),
  doNotCall: generateTextVariations("do_not_call"),
  whitelist: generateTextVariations("whitelist"),
  outOfTimezone: generateTextVariations("out_of_timezone"),
  insufficientCredits: generateTextVariations("insufficient_credits"),
  entityMissing: generateTextVariations("entity_missing"),
  delivered: generateTextVariations("delivered"),
  replied: generateTextVariations("replied"),
  opened: generateTextVariations("opened"),
  clicked: generateTextVariations("clicked"),
  bounced: generateTextVariations("bounced"),
  duplicate: generateTextVariations("duplicate"),
  unsubscribed: generateTextVariations("unsubscribed"),
  verified: generateTextVariations("verified"),
  unverified: generateTextVariations("unverified"),
  open: generateTextVariations("open"),
  merged: generateTextVariations("merged"),
  closed: generateTextVariations("closed"),
  approved: generateTextVariations("approved"),
  queued: generateTextVariations("queued"),
  requestChanges: generateTextVariations("request_changes"),
  creating: generateTextVariations("creating"),
  running: generateTextVariations("running"),
  pausing: generateTextVariations("pausing"),
  resuming: generateTextVariations("resuming"),
  archiving: generateTextVariations("archiving"),
  archived: generateTextVariations("archived"),
  deleted: generateTextVariations("deleted"),
  underReview: generateTextVariations("under_review"),
  rejected: generateTextVariations("rejected"),
  paid: generateTextVariations("paid"),
  created: generateTextVariations("created"),
  delayed: generateTextVariations("delayed"),
  void: generateTextVariations("void"),
  uncollectible: generateTextVariations("uncollectible"),
  present: generateTextVariations("present"),
  leave: generateTextVariations("leave"),
  holiday: generateTextVariations("holiday"),
  weekend: generateTextVariations("weekend"),
  openRequest: generateTextVariations("open_request"),
  casualLeaves: generateTextVariations("casual_leaves"),
  sickLeaves: generateTextVariations("sick_leaves"),
  paidLeave: generateTextVariations("paid_leave"),
  unpaidLeave: generateTextVariations("unpaid_leave"),
  atRisk: generateTextVariations("at_risk"),
  onTrack: generateTextVariations("on_track"),
  low: generateTextVariations("low"),
  high: generateTextVariations("high"),
  medium: generateTextVariations("medium"),
};

export const priorityTextColorMapping = {
  low: generateTextVariations("low"),
  medium: generateTextVariations("medium"),
  high: generateTextVariations("high"),
};

export const statusColorMap = {
  busy: statusTagProviderColors.RED,
  ended: statusTagProviderColors.RED,
  skipped: statusTagProviderColors.RED,
  noAnswer: statusTagProviderColors.RED,
  pending: statusTagProviderColors.GREY,
  failed: statusTagProviderColors.RED,
  paused: statusTagProviderColors.YELLOW,
  dialing: statusTagProviderColors.PURPLE,
  callFailed: statusTagProviderColors.RED,
  cancelled: statusTagProviderColors.RED,
  voicemail: statusTagProviderColors.BLUE,
  connected: statusTagProviderColors.GREEN,
  disconnected: statusTagProviderColors.RED,
  notStarted: statusTagProviderColors.GREY,
  ringing: statusTagProviderColors.PURPLE,
  completed: statusTagProviderColors.GREEN,
  inProgress: statusTagProviderColors.PURPLE,
  transferred: statusTagProviderColors.BLUE,
  callCompleted: statusTagProviderColors.GREEN,
  voicemailDropped: statusTagProviderColors.BLUE,
  enabled: statusTagProviderColors.GREEN,
  disabled: statusTagProviderColors.YELLOW,
  deprecated: statusTagProviderColors.RED,
  draft: statusTagProviderColors.GREY,
  active: statusTagProviderColors.GREEN,
  scheduled: statusTagProviderColors.YELLOW,
  scheduledProcessing: statusTagProviderColors.PURPLE,
  sent: statusTagProviderColors.GREEN,
  inactive: statusTagProviderColors.GREY,
  doNotCall: statusTagProviderColors.RED,
  whitelist: statusTagProviderColors.GREY,
  outOfTimezone: statusTagProviderColors.RED,
  insufficientCredits: statusTagProviderColors.RED,
  entityMissing: statusTagProviderColors.GREY,
  delivered: statusTagProviderColors.GREEN,
  replied: statusTagProviderColors.GREEN,
  opened: statusTagProviderColors.GREEN,
  clicked: statusTagProviderColors.GREEN,
  bounced: statusTagProviderColors.RED,
  duplicate: statusTagProviderColors.YELLOW,
  unsubscribed: statusTagProviderColors.BROWN,
  verified: statusTagProviderColors.GREEN,
  unverified: statusTagProviderColors.YELLOW,
  open: statusTagProviderColors.GREEN,
  merged: statusTagProviderColors.PURPLE,
  closed: statusTagProviderColors.RED,
  approved: statusTagProviderColors.GREEN,
  queued: statusTagProviderColors.YELLOW,
  requestChanges: statusTagProviderColors.YELLOW,
  creating: statusTagProviderColors.BLUE,
  running: statusTagProviderColors.GREEN,
  pausing: statusTagProviderColors.YELLOW,
  resuming: statusTagProviderColors.PURPLE,
  archiving: statusTagProviderColors.GREY,
  archived: statusTagProviderColors.GREY,
  deleted: statusTagProviderColors.RED,
  underReview: statusTagProviderColors.PURPLE,
  rejected: statusTagProviderColors.RED,
  paid: statusTagProviderColors.GREEN,
  created: statusTagProviderColors.GREY,
  delayed: statusTagProviderColors.YELLOW,
  void: statusTagProviderColors.RED,
  uncollectible: statusTagProviderColors.RED,
  present: statusTagProviderColors.GREEN,
  leave: statusTagProviderColors.GREY,
  holiday: statusTagProviderColors.YELLOW,
  weekend: statusTagProviderColors.BLUE,
  openRequest: statusTagProviderColors.PURPLE,
  casualLeaves: statusTagProviderColors.YELLOW,
  sickLeaves: statusTagProviderColors.RED,
  paidLeave: statusTagProviderColors.YELLOW,
  unpaidLeave: statusTagProviderColors.RED,
  atRisk: statusTagProviderColors.RED,
  onTrack: statusTagProviderColors.BLUE,
  low: statusTagProviderColors.BROWN,
  medium: statusTagProviderColors.ORANGE,
  high: statusTagProviderColors.RED,
};

export const priorityBackgroundColorMap = {
  low: priorityTagProviderColors.BLUE_14,
  medium: priorityTagProviderColors.GREEN_14,
  high: priorityTagProviderColors.ORANGE_14,
};

export const priorityBorderColorMap = {
  low: priorityTagProviderColors.BLUE_8,
  medium: priorityTagProviderColors.GREEN_8,
  high: priorityTagProviderColors.ORANGE_8,
};

export const priorityTextColorMap = {
  low: priorityTagProviderColors.BLUE,
  medium: priorityTagProviderColors.GREEN,
  high: priorityTagProviderColors.ORANGE,
};

export const priorityIconMap = {
  low: imagePath.neutral.info.lowPriorityTagIcon,
  medium: imagePath.neutral.info.mediumPriorityTagIcon,
  high: imagePath.neutral.info.highPriorityTagIcon,
};

export const custom_modal_cross_icon_variants = {
  DEFAULT: "default",
  TOP_RIGHT_OUTSIDE: "top_right_outside",
};

export const repliedIconObj = {
  id: "replied",
  icon: imageNames.REPLIED_ICON,
};

export const statusVariant3Config = {
  alpha: {
    bgColor: "var(--alpha-tag-bg)",
    borderColor: "var(--alpha-tag-border)",
    textColor: "var(--alpha-tag-text)",
  },
  beta: {
    bgColor: "var(--beta-tag-bg)",
    borderColor: "var(--beta-tag-border)",
    textColor: "var(--beta-tag-text)",
  },
  ga: {
    bgColor: "var(--ga-tag-bg)",
    borderColor: "var(--ga-tag-border)",
    textColor: "var(--ga-tag-text)",
  },
};

export const statusVariant4Mapping = {
  [statusTagProviderColors.GREEN]: "var(--green-tag-4)",
  [statusTagProviderColors.GREY]: "var(--grey-tag-4)",
  [statusTagProviderColors.PURPLE]: "var(--purple-tag-4)",
  [statusTagProviderColors.YELLOW]: "var(--yellow-tag-4)",
  [statusTagProviderColors.RED]: "var(--red-tag-4)",
  [statusTagProviderColors.BLUE]: "var(--blue-tag-4)",
  [statusTagProviderColors.BROWN]: "var(--brown-tag-4)",
  [statusTagProviderColors.ORANGE]: "var(--orange-tag-4)",
};

export const buttonTypes = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  ACTION: "action",
};

export const buttonSizes = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
};
